import React from "react";
import { StyleSheet, TouchableOpacity } from "react-native";

import Text from "components/Text";
import Input from "components/Input";
import Modal from "components/Modal";
import Shadow from "components/Shadow";
import Button from "components/Button";
import ImgIcon from "components/imgIcon";
import InputLabel from "components/InputLabel";

import { useForm, Controller } from "react-hook-form";

import { sizes, colors, strings } from "assets/constants";
import { icons } from "assets/icons";
import { useFlag } from "hooks";
import { sn } from "utils";

import {
  useCreateTypeOfMeeting,
  useDeleteTypeOfMeeting,
  useUpdateTypeOfMeeting,
} from "clients/apis";
import { useRecoilValue } from "recoil";
import { authAtom } from "stores";
import tinycolor from "tinycolor2";
import ConfirmAlert from "./ConfirmAlert";
import { DeleteAlertComponent, LoadingBox } from "components";

const TypeOfMeetingCard: React.FC<{ typeOfMeeting?: TypeOfMeetingT }> = ({
  typeOfMeeting,
}) => {
  const {
    control,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<{ title: string }>({
    defaultValues: { title: typeOfMeeting?.title || "" },
  });

  const auth = useRecoilValue(authAtom);

  const { flg: edit, on: goToEdit, off: closeEdit } = useFlag();
  const { flg: remove, on: goToRemove, off: closeRemove } = useFlag();

  const createTypeOfMeetingMut = useCreateTypeOfMeeting();
  const updateTypeOfMeetingMut = useUpdateTypeOfMeeting();
  const deleteTypeOfMeetingMut = useDeleteTypeOfMeeting();

  const _onHide = () => {
    reset();
    closeEdit();
  };

  const _onSubmit = () =>
    typeOfMeeting?.id
      ? _onUpdate({ id: typeOfMeeting?.id, ...getValues() })
      : _onCreate({ title: getValues()?.title, admin_id: `${auth?.id}` });

  const _onCreate = (data: CreateTypeOfMeetingReqT) =>
    createTypeOfMeetingMut.mutate(data, { onSuccess: _onHide });

  const _onUpdate = (data: UpdateTypeOfMeetingReqT) =>
    updateTypeOfMeetingMut.mutate(data, { onSuccess: _onHide });

  const _onDeleteConfirm = (close: () => void) => {
    close();
    reset();
    deleteTypeOfMeetingMut.mutate({ id: typeOfMeeting?.id });
  };

  const _renderContent = () => {
    if (!typeOfMeeting) {
      return (
        <TouchableOpacity
          onPress={goToEdit}
          activeOpacity={sizes.tapOpacity}
          style={styles.plusContainer}
        >
          <ImgIcon icon={icons._plus} size={sn(25, 28, 30, 30)} />
        </TouchableOpacity>
      );
    }

    return (
      <>
        {typeOfMeeting?.admin_id !== 1 && (
          <Button
            onPress={goToRemove}
            absolute
            top={5}
            left={5}
            square={25}
            style={{ borderRadius: 12 }}
            icon={{ name: "x", color: colors.danger }}
            bgColor={tinycolor(colors.danger).lighten(35).toString()}
          />
        )}

        <TouchableOpacity
          onPress={goToEdit}
          activeOpacity={sizes.tapOpacity}
          style={styles.header}
        >
          <Text
            align="center"
            style={{ lineHeight: 20 }}
            numberOfLines={2}
            weight="bold"
          >
            {typeOfMeeting?.title}
          </Text>
        </TouchableOpacity>
      </>
    );
  };

  return (
    <>
      <Shadow style={styles.shadowCard}>{_renderContent()}</Shadow>

      <Modal visible={edit} close={closeEdit}>
        <InputLabel label={strings.title} />
        <Controller
          control={control}
          name="title"
          rules={{ required: { value: true, message: strings.fill_input } }}
          render={({ field: { value, onChange } }) => (
            <Input
              value={value}
              onChangeText={onChange}
              imgIcon={{ icon: icons._role }}
              placeholder={strings.insert_title}
              error={errors?.title?.message}
              onSubmitEditing={handleSubmit(_onSubmit)}
            />
          )}
        />

        <Button
          title={{ value: strings.confirm }}
          mt={sizes.space}
          onPress={handleSubmit(_onSubmit)}
          loading={
            createTypeOfMeetingMut.isLoading || updateTypeOfMeetingMut.isLoading
          }
        />
      </Modal>

      <ConfirmAlert
        force
        show={remove}
        hide={closeRemove}
        renderContent={(close) => (
          <DeleteAlertComponent
            title={`آیا میخواهید "${typeOfMeeting?.title}" را حذف کنید؟ `}
            remove={() => _onDeleteConfirm(close)}
            cancel={close}
          />
        )}
      />

      {deleteTypeOfMeetingMut.isLoading && <LoadingBox />}
    </>
  );
};

export default TypeOfMeetingCard;

const styles = StyleSheet.create({
  shadowCard: {
    width: "100%",
    height: "100%",
    overflow: "hidden",
    alignItems: "center",
    borderRadius: sizes.radius.xl,
    backgroundColor: colors.white,
    justifyContent: "space-evenly",
  },

  header: {
    flex: 1,
    padding: sizes.space,
    alignItems: "center",
    justifyContent: "center",
  },

  footer: {
    width: "100%",
    height: sn(40, 60, 60, 60),
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row-reverse",
  },

  actions: { flex: 1, alignItems: "center", justifyContent: "center" },

  plusContainer: {
    flex: 1,
    width: "100%",
    height: "100%",
    alignItems: "center",
    justifyContent: "center",
  },

  row: { flexDirection: "row-reverse", alignSelf: "center" },
});
